import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Clock, Calendar, Bell, Play, Settings, CheckCircle2, 
  AlertTriangle, Loader2, RefreshCw, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`
}));

export default function DigestSettings() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  
  const [config, setConfig] = useState({
    enabled: false,
    day_of_week: 'mon',
    hour: 8,
    minute: 0,
    timezone: 'America/New_York',
    last_run: '',
    next_run: ''
  });

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/digest/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        enabled: config.enabled,
        day_of_week: config.day_of_week,
        hour: config.hour,
        minute: config.minute
      });
      
      const response = await fetch(`${API_URL}/api/digest/schedule?${params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Schedule saved', {
          description: config.enabled 
            ? `Digest will run every ${DAYS_OF_WEEK.find(d => d.value === config.day_of_week)?.label} at ${HOURS.find(h => h.value === config.hour)?.label}`
            : 'Automated digest disabled'
        });
        setConfig(prev => ({
          ...prev,
          next_run: result.config?.next_run || ''
        }));
        fetchConfig();
      } else {
        const error = await response.json();
        toast.error('Failed to save', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error saving schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const response = await fetch(`${API_URL}/api/digest/run-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Digest executed', {
          description: 'Check your notifications for the weekly summary'
        });
        fetchConfig();
      } else {
        const error = await response.json();
        toast.error('Failed to run digest', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error running digest');
    } finally {
      setRunningNow(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-[#00b0ab] border-t-transparent rounded-full" />
      </div>
    );
  }

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="space-y-6" data-testid="digest-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-[#00b0ab] to-[#008a86] bg-clip-text text-transparent">
            Weekly Digest Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure automated weekly staffing digest
          </p>
        </div>
        <Button 
          onClick={handleRunNow} 
          disabled={runningNow}
          className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
          data-testid="run-digest-now-btn"
        >
          {runningNow ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Run Now
            </>
          )}
        </Button>
      </div>

      {/* Status Card */}
      <Card className={`${config.enabled ? 'bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-200' : 'bg-gradient-to-br from-slate-500/10 to-gray-500/10 border-slate-200'}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`h-12 w-12 rounded-xl ${config.enabled ? 'bg-emerald-500/20' : 'bg-slate-500/20'} flex items-center justify-center`}>
                {config.enabled ? (
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-slate-500" />
                )}
              </div>
              <div>
                <p className="font-semibold">{config.enabled ? 'Automated Digest Active' : 'Automated Digest Disabled'}</p>
                <p className="text-sm text-muted-foreground">
                  {config.enabled 
                    ? `Runs every ${DAYS_OF_WEEK.find(d => d.value === config.day_of_week)?.label} at ${HOURS.find(h => h.value === config.hour)?.label}`
                    : 'Enable to receive automatic weekly summaries'}
                </p>
              </div>
            </div>
            <Badge className={config.enabled ? 'bg-emerald-500' : 'bg-slate-500'}>
              {config.enabled ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-[#00b0ab]" />
            Schedule Configuration
          </CardTitle>
          <CardDescription>
            The weekly digest will generate notifications for burnout risks, PTO coverage, hiring needs, and compliance alerts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-[#00b0ab]" />
              <div>
                <Label className="font-medium">Enable Automated Digest</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically generate weekly staffing reports
                </p>
              </div>
            </div>
            <Switch 
              checked={config.enabled}
              onCheckedChange={(v) => setConfig({...config, enabled: v})}
              disabled={!canEdit}
            />
          </div>
          
          {/* Schedule Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Day of Week
              </Label>
              <Select 
                value={config.day_of_week}
                onValueChange={(v) => setConfig({...config, day_of_week: v})}
                disabled={!canEdit || !config.enabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map(day => (
                    <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Time
              </Label>
              <Select 
                value={config.hour?.toString()}
                onValueChange={(v) => setConfig({...config, hour: parseInt(v)})}
                disabled={!canEdit || !config.enabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map(hour => (
                    <SelectItem key={hour.value} value={hour.value.toString()}>{hour.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Input 
                value={config.timezone}
                disabled
                className="bg-slate-50"
              />
            </div>
          </div>
          
          {/* Last/Next Run Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
              <p className="text-sm text-muted-foreground">Last Run</p>
              <p className="font-medium">
                {config.last_run 
                  ? new Date(config.last_run).toLocaleString()
                  : 'Never'}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
              <p className="text-sm text-muted-foreground">Next Scheduled Run</p>
              <p className="font-medium">
                {config.next_run 
                  ? new Date(config.next_run).toLocaleString()
                  : config.enabled ? 'Calculating...' : 'N/A'}
              </p>
            </div>
          </div>
          
          {/* Save Button */}
          {canEdit && (
            <div className="flex justify-end pt-4">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save Schedule
              </Button>
            </div>
          )}
          
          {!canEdit && (
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Only admins and managers can modify schedule settings.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* What's Included */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#00b0ab]" />
            What's in the Weekly Digest?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <p className="font-medium">Burnout Risk Alerts</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Identifies clinicians operating over capacity
              </p>
            </div>
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                <p className="font-medium">PTO Coverage Needs</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Flags upcoming PTO without coverage plans
              </p>
            </div>
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="h-5 w-5 text-violet-500" />
                <p className="font-medium">Hiring Forecasts</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Projects staffing needs based on census growth
              </p>
            </div>
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <p className="font-medium">Compliance Status</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Alerts when compliance falls below target
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
